import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { appSettingsKeys } from "@/lib/query";

export interface RecipientTeams {
  postDelivery: string;
  deliveryCompliance: string;
}

export function useRecipientTeams() {
  return useQuery({
    queryKey: appSettingsKeys.recipientTeams,
    queryFn: async (): Promise<RecipientTeams> => {
      const res = await fetch("/api/settings/recipient-teams");
      if (!res.ok) throw new Error("Failed to load recipient teams");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useSaveRecipientTeams() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: RecipientTeams) => {
      const res = await fetch("/api/settings/recipient-teams", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to save recipient teams");
      const json = await res.json();
      return json.data as RecipientTeams;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(appSettingsKeys.recipientTeams, data);
      toast.success("Recipient teams saved");
    },
    onError: () => {
      toast.error("Failed to save recipient teams");
    },
  });
}

export interface GuidanceRequestTemplate {
  template: string;
}

export function useGuidanceRequestTemplate() {
  return useQuery({
    queryKey: appSettingsKeys.guidanceRequestTemplate,
    queryFn: async (): Promise<GuidanceRequestTemplate> => {
      const res = await fetch("/api/settings/guidance-request-template");
      if (!res.ok) throw new Error("Failed to load guidance request template");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useSaveGuidanceRequestTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: GuidanceRequestTemplate) => {
      const res = await fetch("/api/settings/guidance-request-template", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to save guidance request template");
      const json = await res.json();
      return json.data as GuidanceRequestTemplate;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(appSettingsKeys.guidanceRequestTemplate, data);
      toast.success("Guidance request template saved");
    },
    onError: () => {
      toast.error("Failed to save guidance request template");
    },
  });
}
